/**
 * What: Rotates a random short caption from a fixed phrase pool while a build stage is active.
 * Transport / data-flow: Pure client-side timer over a caller-supplied phrase list — no network.
 *   Returns null while inactive so a completed stage renders nothing extra.
 */
import { useEffect, useState } from 'react'

const ROTATE_INTERVAL_MS = 2600

function pickPhrase(phrases: readonly string[], excluding?: string | null): string {
  if (phrases.length === 1) return phrases[0]!
  let next = phrases[Math.floor(Math.random() * phrases.length)]!
  while (next === excluding) {
    next = phrases[Math.floor(Math.random() * phrases.length)]!
  }
  return next
}

/**
 * Picks a random phrase from `phrases` on activation and re-picks (never immediately repeating)
 * every ~2.6s while `active` stays true. Returns null while `active` is false.
 */
export function useRotatingProgressPhrase(phrases: readonly string[], active: boolean): string | null {
  const [phrase, setPhrase] = useState<string | null>(() => (active ? pickPhrase(phrases) : null))

  useEffect(() => {
    if (!active) {
      setPhrase(null)
      return
    }
    setPhrase((current) => current ?? pickPhrase(phrases))
    const id = setInterval(() => {
      setPhrase((current) => pickPhrase(phrases, current))
    }, ROTATE_INTERVAL_MS)
    return () => clearInterval(id)
  }, [active, phrases])

  return phrase
}
